// Chart color palette (RET-4289).
//
// The single place JS learns chart colors. Every literal lives in custom/charts.scss
// (:root + the color-mode(dark) override); this module reads those CSS variables off
// document.documentElement, so values are ALWAYS correct for the active theme — the theme
// toggle forces a full reload, so a read at chart-build time can't go stale.
//
// Consumers: theme.js (global Chart.js defaults + the dataset theming plugin) and the bespoke
// renderers (calls_receivables_and_payables_combo) and charts_js_controller chrome. Nothing here should
// hardcode a color — add it to charts.scss and read it through chartPalette().

// getComputedStyle().getPropertyValue returns the computed value with var() substituted, so
// charts.scss can reference Bootstrap brand tokens (var(--bs-primary), etc.) and JS still reads a
// resolved literal color. Fall back to a neutral when the stylesheet isn't resolved yet (first
// paint, tests) — substitution preserves leading whitespace, hence the .trim() below.
export function cssVar(name, fallback) {
    if (typeof document === 'undefined' || !document.documentElement) return fallback
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    return value || fallback
}

// Resolved palette for the active theme. Call at chart-build time (cheap; just var reads).
export function chartPalette() {
    return {
        // Data-viz series hues (theme-independent).
        cyan: cssVar('--chart-cyan', '#00e5ff'),
        green: cssVar('--chart-green', '#4ade80'),
        teal: cssVar('--chart-teal', '#14b8a6'),
        amber: cssVar('--chart-amber', '#fbbf24'),
        orange: cssVar('--chart-orange', '#d97706'),
        violet: cssVar('--chart-violet', '#8e84ff'),
        dark: cssVar('--chart-dark', '#475569'),

        // Chrome (flips with color mode).
        ink: cssVar('--chart-ink', 'rgba(33,37,41,0.75)'), // Bootstrap body-secondary (light)
        labelInk: cssVar('--chart-label-ink', 'rgba(33,37,41,0.75)'), // Bootstrap body-secondary (light)
        totalInk: cssVar('--chart-total-ink', 'rgba(33,37,41,0.75)'), // Bootstrap body-secondary (light)
        grid: cssVar('--chart-grid', 'rgba(128,128,128,0.18)'),
        border: cssVar('--chart-border', 'rgba(128,128,128,0.18)'),
        tooltipBg: cssVar('--chart-tooltip-bg', 'rgba(10,13,16,0.96)'),

        // Calls series stroke + area-fill stops (flip with color mode).
        callsLine: cssVar('--chart-calls-line', '#0e7490'),
        areaFillTop: cssVar('--chart-area-fill-top', 'rgba(14,116,144,0.16)'),
        areaFillBottom: cssVar('--chart-area-fill-bottom', 'rgba(14,116,144,0)'),

        // Choropleth state outline (flips with color mode).
        choroplethBorder: cssVar('--chart-choropleth-border', 'rgba(125,211,230,0.35)'),

        // Neutral placeholder.
        emptyFill: cssVar('--chart-empty-fill', '#e0e0e0'),
        emptyBorder: cssVar('--chart-empty-border', '#b0b0b0'),
    }
}

// Order of brand hues for "don't care" categorical series (auto-color cycle, below).
export const CATEGORICAL = ['green', 'dark', 'amber', 'cyan', 'orange', 'teal', 'violet']

// Resolve CATEGORICAL keys to the active theme's colors.
export function categoricalColors(palette) {
    return CATEGORICAL.map((key) => palette[key])
}

// Parse a CSS hex (#rgb / #rrggbb) or rgb()/rgba() string into [r, g, b] (0–255). Lets us
// derive alpha-graded variants of a themed color (e.g. the choropleth ramp) instead of
// hardcoding a second copy of the same hue. Falls back to cyan if the string is unrecognized.
function toRgb(color) {
    const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
    if (hex) {
        let h = hex[1]
        if (h.length === 3)
            h = h
                .split('')
                .map((c) => c + c)
                .join('')
        return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16))
    }
    const parts = color.match(/[\d.]+/g)
    return parts ? parts.slice(0, 3).map(Number) : [0, 229, 255]
}

// The palette color scaled toward black by `amount` (0–1). Unlike an alpha fade, the shaded end
// stays opaque, so it reads as the same hue in shadow regardless of what's behind the bar.
function darken(color, amount) {
    const [r, g, b] = toRgb(color).map((channel) => Math.round(channel * (1 - amount)))
    return `rgb(${r}, ${g}, ${b})`
}

// The palette color scaled toward white by `amount` (0–1) — darken()'s lit twin, for the bright
// cap on a vertical bar's tip.
function lighten(color, amount) {
    const [r, g, b] = toRgb(color).map((channel) => Math.round(channel + (255 - channel) * amount))
    return `rgb(${r}, ${g}, ${b})`
}

// The lit cap on a vertical bar's value tip: its on-canvas height (px) and how far toward white
// it brightens. Baked into the fill gradient as a hard stop (two stops at one offset) rather
// than a top border: a border's color would also feed the HTML legend chips and the tooltip
// swatches (both read dataset.borderColor), while a fill-side cap keeps those the true hue.
const BAR_CAP_HEIGHT = 1.5
const BAR_CAP_LIGHTEN = 0.3

// Choropleth fill ramp: the brand cyan whose ALPHA scales from 0 (transparent — the themed
// card background shows through, so zero/no-data states fade into the dark or light bg like
// the mockup) up to 1 (full cyan). chartjs-chart-geo calls this per feature with the value
// normalized to [0, 1]; it also drives the footer gradient legend (#renderColorScaleLegend).
export function choroplethInterpolate(palette) {
    const [r, g, b] = toRgb(palette.cyan)
    return (t) => `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, t))})`
}

// Scriptable, top→bottom CanvasGradient against the live plot area. Chart.js invokes the
// returned function every render (including resize), so the gradient tracks the real chartArea
// rather than a canvas height captured once at construction. `stops` is [[offset, color], ...]
// — colors are palette values; this helper owns only the geometry.
export function verticalGradient(stops) {
    return (context) => {
        const { ctx, chartArea } = context.chart
        if (!chartArea) return // pre-layout (first paint / tests); Chart.js retries next frame
        const grad = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        stops.forEach(([offset, color]) => grad.addColorStop(offset, color))
        return grad
    }
}

// Final pixel span of a bar along its value axis: { head: value tip, base: baseline }
// Lets us add a depth effect to bars.
function barValueSpan(context) {
    const controller = context.chart.getDatasetMeta(context.datasetIndex)?.controller
    const pixels = controller?._calculateBarValuePixels?.(context.dataIndex)
    if (!pixels || !Number.isFinite(pixels.base) || !Number.isFinite(pixels.head)) return null
    return pixels.head === pixels.base ? null : pixels
}

// Bar "depth" fill: a per-bar gradient along the column — a lit cap at the value tip, the full
// hue just under it, darkening toward the base (in shadow).
export function barDepthFill(color) {
    return (context) => {
        const span = barValueSpan(context)
        if (!span) return color
        const grad = context.chart.ctx.createLinearGradient(0, span.head, 0, span.base)
        const capEnd = Math.min(BAR_CAP_HEIGHT / Math.abs(span.base - span.head), 1)
        grad.addColorStop(0, lighten(color, BAR_CAP_LIGHTEN))
        grad.addColorStop(capEnd, lighten(color, BAR_CAP_LIGHTEN))
        grad.addColorStop(capEnd, color)
        grad.addColorStop(1, darken(color, 0.22))
        return grad
    }
}

// Horizontal-bar (indexAxis 'y') variant of the depth fill
// The gradient runs along each bar's length (its baseline `base` → its value tip `head`)
// Darkening the hue toward the baseline (in shadow) and brightening to the full hue at the tip
export function horizontalBarDepthFill(color) {
    return (context) => {
        const span = barValueSpan(context)
        if (!span) return color
        const grad = context.chart.ctx.createLinearGradient(span.base, 0, span.head, 0)
        grad.addColorStop(0, darken(color, 0.22))
        grad.addColorStop(1, color)
        return grad
    }
}

// Stacked horizontal bars: a per-bar TOP→BOTTOM gradient across the bar's OWN thickness (its row
// height, element.y ± height/2).
export function horizontalStackedDepthFill(color) {
    return (context) => {
        const element = context.element
        if (!element || !Number.isFinite(element.y) || !Number.isFinite(element.height))
            return color
        const top = element.y - element.height / 2
        const grad = context.chart.ctx.createLinearGradient(0, top, 0, top + element.height)
        grad.addColorStop(0, color)
        grad.addColorStop(1, darken(color, 0.22))
        return grad
    }
}

// Corner radius (px) for the rounded cap on a bar's growing end (see barCapBorderRadius).
const BAR_CAP_RADIUS = 3

// Scriptable `elements.bar.borderRadius` shared by every bar chart. Handles:
// - Vertical columns round their top cap
// - Horizontal rows round both ends into a pill
// - Stacked bars split the pill across the stack
export function barCapBorderRadius(horizontal, stacked) {
    const radius = BAR_CAP_RADIUS
    const cap = horizontal
        ? { topLeft: radius, bottomLeft: radius, topRight: radius, bottomRight: radius }
        : { topLeft: radius, topRight: radius }
    return (context) => {
        if (!stacked) return cap
        const { chart, dataset, datasetIndex, dataIndex } = context
        let first = -1
        let last = -1
        chart.data.datasets.forEach((ds, i) => {
            if (!chart.isDatasetVisible(i) || ds.stack !== dataset.stack) return
            if (!ds.data[dataIndex]) return
            if (first === -1) first = i
            last = i
        })
        if (!horizontal) return datasetIndex === last ? cap : 0
        return {
            ...(datasetIndex === first && { topLeft: radius, bottomLeft: radius }),
            ...(datasetIndex === last && { topRight: radius, bottomRight: radius }),
        }
    }
}

// Resolve `var(--chart-*)` color tokens in dataset colors against the active theme.
// The canvas can't read CSS custom properties (and the Rails backend can't read the
// browser's theme), so a dataset names the token — e.g. backgroundColor: 'var(--chart-green)'
// — and we swap in the resolved value here, keeping one source of truth (charts.scss) and
// letting the color flip with color mode. Resolved literals pass through untouched, so
// re-running on every update is safe.
function resolveCssVarColors(data) {
    if (!data || !data.datasets) return
    const resolve = (value) => {
        if (Array.isArray(value)) return value.map(resolve)
        if (typeof value !== 'string') return value
        const match = value.match(/^var\((--[\w-]+)\)$/)
        return match ? cssVar(match[1], value) : value
    }
    data.datasets.forEach((dataset) => {
        dataset.backgroundColor = resolve(dataset.backgroundColor)
        dataset.borderColor = resolve(dataset.borderColor)
    })
}

// Line datasets that opt into `fill` get the combo chart's cyan area-fill look: a
// top→bottom gradient (verticalGradient above) under a calls-line stroke. Colors come
// from the palette so the fill tracks the active theme (the CSS vars flip in dark mode).
// Only unstyled series are themed — an explicit color in the dataset still wins.
function applyLineAreaGradients(data, type) {
    if (type !== 'line' || !data || !data.datasets) return
    const palette = chartPalette()
    data.datasets.forEach((dataset) => {
        if (!dataset.fill) return
        if (dataset.backgroundColor == null) {
            dataset.backgroundColor = verticalGradient([
                [0, palette.areaFillTop],
                [1, palette.areaFillBottom],
            ])
        }
        if (dataset.borderColor == null) dataset.borderColor = palette.callsLine
    })
}

// Dataset theming pipeline — the brand replacement for Chart.js' built-in `colors` plugin.
// Runs before every layout, so a Turbo data reload re-themes the fresh datasets with no
// per-call-site wiring. Three idempotent passes, explicit dataset colors always winning:
//   resolve `var(--chart-*)` tokens a presenter named in dataset colors,
//   give filled line series the themed area-gradient look,
//   auto-color any still-unstyled series with the brand palette cycle.
export function datasetThemingPlugin(cycle) {
    return {
        id: 'retreaverTheming',
        beforeLayout(chart) {
            if (chart.config.type === 'choropleth') return // geo owns its color scale
            resolveCssVarColors(chart.data)
            applyLineAreaGradients(chart.data, chart.config.type)
            chart.data.datasets.forEach((dataset, index) => {
                if (dataset.borderColor != null || dataset.backgroundColor != null) return
                const meta = chart.getDatasetMeta(index)
                const type = (meta && meta.type) || chart.config.type
                const color = cycle[index % cycle.length]
                if (type === 'pie' || type === 'doughnut' || type === 'polarArea') {
                    dataset.backgroundColor = dataset.data.map((_, i) => cycle[i % cycle.length])
                } else if (type === 'bar') {
                    // Bars get a depth gradient along their length: full hue at the value tip,
                    // darker at the base. Vertical bars add a bright top-edge highlight cap.
                    // Horizontal bars (indexAxis 'y', the ranked-breakdown charts) skip that cap:
                    // unstacked bars take the same length gradient, while stacked bars take a
                    // top→bottom thickness gradient so each end-to-end segment stays uniformly
                    // shaded instead of banding by length.
                    const horizontal = chart.options.indexAxis === 'y'
                    const scales = chart.options.scales
                    const stacked = !!(scales?.x?.stacked || scales?.y?.stacked)
                    if (!horizontal) {
                        dataset.backgroundColor = barDepthFill(color)
                    } else {
                        dataset.backgroundColor = stacked
                            ? horizontalStackedDepthFill(color)
                            : horizontalBarDepthFill(color)
                    }
                    dataset.borderWidth = 0
                    dataset.borderColor = color
                } else {
                    dataset.borderColor = color
                    dataset.backgroundColor = color
                }
            })
        },
    }
}
